import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PageDemandSideSnapshot {
  label: string;
  source: string;
  confidence: 'high' | 'medium' | 'low' | 'none';
  detail?: string;
}

export interface PageDemandRealizationSnapshot {
  label: string;
  source: string;
  confidence: 'high' | 'medium' | 'low' | 'none';
  position: number | null;
  searchVolume: number | null;
  detail?: string;
}

export interface PageDemandSnapshot {
  claim: PageDemandSideSnapshot | null;
  claimIntentTokens?: string[];
  realization: PageDemandRealizationSnapshot | null;
  ambientTopic: PageDemandSideSnapshot | null;
  splitReasons: string[];
  computedAt: string | null;
}

export interface PageRealizationCeilingDriverSnapshot {
  keyword: string;
  searchVolume: number;
  matchingTokens: string[];
  upsideAtTargetRank: number;
}

export interface PageRealizationSnapshot {
  realizedVisibility: number;
  realizedSource: 'etv' | 'traffic_proxy';
  demandCeiling: number | null;
  ceiling: number;
  realizationRatio: number | null;
  gap: number | null;
  gapGate: 'scored' | 'low_confidence' | 'pending' | 'unscored';
  ceilingDriver: PageRealizationCeilingDriverSnapshot | null;
  alignedKeywordCount: number;
}

export interface PageHealthSnapshot {
  band: 'critical' | 'weak' | 'ok' | 'best' | 'unscored';
  reasons: string[];
  model: 'realization_gap' | 'eligibility' | 'unscored';
}

export interface SecondaryPageHealthSnapshot {
  band: 'critical' | 'weak' | 'ok' | 'best' | 'unscored' | null;
  model: 'none' | 'freshness_hold' | 'earned_visibility';
  reasons: string[];
  recentlyUpdated: boolean;
  unscoredSegment:
    | 'no_claim'
    | 'low_confidence_claim'
    | 'no_market_data'
    | 'no_aligned_ceiling'
    | 'immaterial_ceiling'
    | null;
}

export type PageSerpQuerySource = 'realization' | 'ceiling_driver' | 'claim_fallback';
export type PageSerpMatchType = 'exact_url' | 'domain' | 'none';

export interface PageSerpResultRow {
  position: number;
  domain: string;
  url: string;
  title: string;
  description?: string;
  highlighted?: string[];
  publishedAt?: string | null;
}

export type PageIntentDiagnosisBulletKind =
  | 'serp_family'
  | 'placement'
  | 'lexical_gap'
  | 'lexical_misuse'
  | 'claim_mismatch'
  | 'mixed_serp'
  | 'freshness'
  | 'vocabulary_fragmented'
  | 'no_serp';

export interface SerpCollocationHead {
  head: string;
  neighbors: string[];
}

export interface PageIntentDiagnosisBullet {
  text: string;
  kind: PageIntentDiagnosisBulletKind;
  severity: 'info' | 'warn' | 'success';
}

export interface PageIntentSectionPreview {
  label: string;
  text: string;
}

export interface SerpWinnerExcerpt {
  position: number;
  domain: string;
  excerpt: string;
  highlightTerms: string[];
}

export interface SerpMixedIntentSample {
  position: number;
  domain: string;
  family: string;
}

export interface SerpRowIntent {
  position: number;
  family: string;
}

export interface PageIntentDiagnosis {
  serpFamily: string;
  serpConsensusRatio: number;
  serpMixedIntentSamples: SerpMixedIntentSample[];
  serpRowIntents: SerpRowIntent[];
  bestSegmentLabel: string;
  bestSegmentFamily: string;
  intentAlignedWithSerp: boolean;
  serpLexicalTerms: string[];
  serpVocabularyShared: boolean;
  queryContextTokens: string[];
  presentLexicalTerms: string[];
  misalignedLexicalTerms: string[];
  missingTerms: string[];
  serpCollocationHeads: SerpCollocationHead[];
  bullets: PageIntentDiagnosisBullet[];
  yourBestSection: PageIntentSectionPreview | null;
  yourIntroSection: PageIntentSectionPreview | null;
  serpWinnerExcerpts: SerpWinnerExcerpt[];
}

export interface PageSerpSnapshot {
  query: string;
  queryDisplay: string;
  querySource: PageSerpQuerySource;
  locationCode: number;
  languageName: string;
  results: PageSerpResultRow[];
  yourPosition: number | null;
  yourUrl: string | null;
  matchType: PageSerpMatchType;
  fetchedAt: string;
  dataSource: 'database' | 'api';
  /** Labs keyword difficulty 0–100 for the target query (SERP competitiveness). */
  keywordDifficulty: number | null;
  keywordDifficultyDataSource: 'database' | 'api' | null;
}

export interface UrlAnalysisResponse {
  success: boolean;
  data: {
    url: string;
    urlId?: string;
    planClusters?: string[];
    analysis: any | null;
    pageDemand?: PageDemandSnapshot | null;
    pageRealization?: PageRealizationSnapshot | null;
    pageHealth?: PageHealthSnapshot | null;
    pageSecondaryHealth?: SecondaryPageHealthSnapshot | null;
    pageSerp?: PageSerpSnapshot | null;
    pageIntentDiagnosis?: PageIntentDiagnosis | null;
    message?: string;
    status: 'completed' | 'pending';
  };
  timestamp: string;
}

export interface RefetchPageAnalysisResponse {
  success: boolean;
  data: {
    url: string;
    status: 'completed' | 'pending';
    taskId: string | null;
    metadata?: {
      dataSource: 'database' | 'api';
      lastUpdated?: string;
      taskId?: string;
    };
  };
  timestamp: string;
}

export interface UrlAnalysisStatusResponse {
  success: boolean;
  data: {
    status: 'completed' | 'pending' | 'failed';
    analysis?: any;
    error?: string;
    metadata?: {
      dataSource?: 'database' | 'api';
      lastUpdated?: string;
    };
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class DataForSEOService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * Get the latest PageAnalysis for a specific URL from the database.
   * Calls GET /api/v1/urls/analysis?url=<encoded>.
   *
   * Note: Analyses are created server-side via the page comparison flow
   * (POST /api/v1/urls/compare → DataForSEO pingback → PageAnalysis).
   */
  getUrlAnalysis(url: string): Observable<UrlAnalysisResponse> {
    const encodedUrl = encodeURIComponent(url);
    return this.http.get<UrlAnalysisResponse>(`${this.API_URL}/urls/analysis?url=${encodedUrl}`);
  }

  /** Dev-only: force a fresh single-page analysis for this URL. */
  refetchSinglePageAnalysis(url: string): Observable<RefetchPageAnalysisResponse> {
    return this.http.post<RefetchPageAnalysisResponse>(
      `${this.API_URL}/urls/process/refetch-page-analysis`,
      { url }
    );
  }

  /** Poll backend task status for a page-analysis task ID. */
  getUrlAnalysisStatus(taskId: string): Observable<UrlAnalysisStatusResponse> {
    return this.http.get<UrlAnalysisStatusResponse>(`${this.API_URL}/urls/analysis-status/${taskId}`);
  }

  /** Title + H1 from stored PageAnalysis for supporting-page token similarity. */
  getSupportingPageLexical(urls: string[]): Observable<{
    success: boolean;
    data: { byUrl: Record<string, { title: string; h1: string }> };
  }> {
    return this.http.post<{
      success: boolean;
      data: { byUrl: Record<string, { title: string; h1: string }> };
    }>(`${this.API_URL}/urls/supporting-page-lexical`, { urls });
  }

  /** Supporting-page opportunity eval: embedding page-match + content-gap scoring. */
  postSupportingPagesOpportunityEval(body: {
    focalUrl: string;
    competitorUrls: string[];
    yourCandidateUrls: string[];
    v1ByCompetitor: Array<{
      competitorUrl: string;
      similarUrl: string | null;
      score: number | null;
      v1CompetitorPreview?: string;
      v1YourMatchPreview?: string | null;
    }>;
  }): Observable<{ success: boolean; data: SupportingPagesV2EvalData }> {
    return this.http.post<{ success: boolean; data: SupportingPagesV2EvalData }>(
      `${this.API_URL}/urls/supporting-pages-opportunity-eval`,
      body
    );
  }

  getSupportingPagesByTopicSnapshot(url: string): Observable<{
    success: boolean;
    cached: boolean;
    data: SupportingPagesByTopicSnapshotData | null;
  }> {
    return this.http.get<{
      success: boolean;
      cached: boolean;
      data: SupportingPagesByTopicSnapshotData | null;
    }>(`${this.API_URL}/urls/supporting-pages-by-topic-snapshot`, {
      params: { url }
    });
  }

  getSupportingPagesOpportunitySnapshot(url: string): Observable<{
    success: boolean;
    cached: boolean;
    data: SupportingPagesV2EvalData | null;
  }> {
    return this.http.get<{
      success: boolean;
      cached: boolean;
      data: SupportingPagesV2EvalData | null;
    }>(`${this.API_URL}/urls/supporting-pages-opportunity-snapshot`, {
      params: { url }
    });
  }

  rebuildSupportingPagesByTopicSnapshot(focalUrl: string): Observable<{
    success: boolean;
    message?: string;
    data: SupportingPagesByTopicSnapshotData | null;
  }> {
    return this.http.post<{
      success: boolean;
      message?: string;
      data: SupportingPagesByTopicSnapshotData | null;
    }>(`${this.API_URL}/urls/supporting-pages-by-topic/rebuild`, { focalUrl });
  }

  rebuildSupportingPagesOpportunitySnapshot(focalUrl: string, options?: { skipV1?: boolean }): Observable<{
    success: boolean;
    message?: string;
    data: {
      supportingPages: SupportingPagesByTopicSnapshotData | null;
      opportunities: SupportingPagesV2EvalData | null;
    };
  }> {
    return this.http.post<{
      success: boolean;
      message?: string;
      data: {
        supportingPages: SupportingPagesByTopicSnapshotData | null;
        opportunities: SupportingPagesV2EvalData | null;
      };
    }>(`${this.API_URL}/urls/supporting-pages-opportunities/rebuild`, {
      focalUrl,
      skipV1: options?.skipV1 === true
    });
  }

}

/**
 * One competitor section measured against your whole focal page.
 * `sectionCovered` = topic addressed somewhere on your page;
 * `sectionSimilarity` = similarity to `closestMy*` (0–1);
 * `closestMy*` = nearest focal section when verifying a gap;
 * `absentTerms` = salient phrases found nowhere on your page.
 */
export interface HeadingPathSegment {
  level: number;
  heading: string;
}

export interface SectionCoverageSiblingLeaf {
  sectionNumber?: number;
  level: number;
  heading: string;
  sectionCovered: boolean;
  isGapLeaf: boolean;
}

export interface SectionCoverageGapMemberPath {
  sectionNumber?: number;
  headingPathSegments?: HeadingPathSegment[];
}

export interface ListInventoryItem {
  label: string;
  normalizedLabel: string;
  source: 'numbered_heading' | 'bullet_list';
  headingLevel?: number | null;
  sectionNumber?: number;
}

export interface ListInventoryDiff {
  detected: boolean;
  focalItemCount: number;
  competitorItemCount: number;
  competitorOnly: ListInventoryItem[];
  focalOnly: ListInventoryItem[];
  onBoth: ListInventoryItem[];
  focalItems: ListInventoryItem[];
  competitorItems: ListInventoryItem[];
}

export interface SectionCoverageRow {
  sectionNumber?: number;
  sectionNumberEnd?: number | null;
  sectionTotal?: number;
  pagePosition?: string;
  competitorHeading: string;
  competitorHeadingPath?: string;
  competitorHeadingPathSegments?: HeadingPathSegment[];
  competitorHeadingLevel?: number;
  /** Consecutive missing sibling sections collapsed for user compare UI. */
  isGapGroup?: boolean;
  groupMemberSectionNumbers?: number[];
  gapAncestorSegments?: HeadingPathSegment[];
  gapMemberPaths?: SectionCoverageGapMemberPath[];
  gapSiblingLeaves?: SectionCoverageSiblingLeaf[];
  listicleSectionRole?: 'tool_root' | 'tool_feature' | null;
  listicleToolLabel?: string | null;
  listicleToolOnFocal?: boolean | null;
  listicleSuppressed?: boolean;
  /** True when section is structural/non-article — excluded from gap scoring. */
  isNonTopical?: boolean;
  nonTopicalReason?: 'generic_heading' | 'author_bio' | 'newsletter_signup' | null;
  sectionCovered: boolean;
  sectionSimilarity: number;
  closestMyHeading?: string | null;
  closestMyHeadingPath?: string | null;
  closestMyHeadingPathSegments?: HeadingPathSegment[] | null;
  closestMyHeadingLevel?: number | null;
  closestMyText?: string | null;
  absentTerms: string[];
  competitorText?: string;
}

export interface SupportingPagesByTopicSnapshotData {
  focalUrl: string;
  urlId: string;
  focalAnchorTopicSlugs: string[];
  focalAnchorTopicLabels: string[];
  domains: Array<{
    domain: string;
    isYourSite: boolean;
    rows: DomainTopicUrlRowSnapshot[];
  }>;
  competitorSetHash: string;
  generatedAt: string;
}

export interface DomainTopicUrlRowSnapshot {
  url: string;
  seoIntentScore: number;
  estimatedOrganicTraffic?: number;
  topics: Array<{
    topicSlug: string;
    topicLabel: string;
    role: 'main' | 'supporting';
  }>;
  similarOnYourSite?: { url: string; score: number };
}

export interface SupportingPagesV2RowResult {
  competitorUrl: string;
  v1SimilarUrl: string | null;
  v1Score: number | null;
  v2PageMatchUrl: string | null;
  v2PageMatchScore: number | null;
  v1PageGap: boolean;
  v2PageGap: boolean;
  disagree: boolean;
  missingSectionCount: number;
  rawMissingSectionCount?: number;
  listicleRelevantMissingSectionCount?: number | null;
  absentTermTotal: number;
  sectionCoverage?: SectionCoverageRow[];
  sectionCoverageGroups?: SectionCoverageRow[];
  listInventoryDiff?: ListInventoryDiff | null;
  focalBodySource: string;
  competitorBodySource: string;
  focalGapSkipped: boolean;
  error: string | null;
  comparedText: SupportingPagesV2ComparedText | null;
  novelVsFocal: string;
  novelVsFocalLabel: string;
  coveredElsewhereUrl: string | null;
  coveredElsewhereSource: string;
  coveredElsewhereLabel: string;
  suggestedAction: string;
  suggestedActionLabel: string;
  opportunityPriority: number;
}

export interface SupportingPagesV2ComparedText {
  pageMatch: {
    v1Competitor: string;
    v1YourMatch: string | null;
    v2CompetitorQuery: string;
    v2YourMatch: string | null;
  };
  focalGap: {
    myContent: string | null;
    competitorContent: string | null;
    skipped: boolean;
    skipReason: string | null;
  };
}

export interface SupportingPagesV2EvalData {
  serviceAvailable: boolean;
  serviceUrl: string;
  serviceVersion: string | null;
  focalUrl: string;
  summary: {
    competitorCount: number;
    v1GapCount: number;
    v2PageGapCount: number;
    disagreementCount: number;
    focalMacroGapTotal: number;
    strongNoveltyCount: number;
    actionableCount: number;
    newSupportingPageCount: number;
  };
  rows: SupportingPagesV2RowResult[];
} 